import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { IDProofingDashboard } from '../components/physicians/IDProofingDashboard';
import { IDProofingStepWrapper } from '../components/physicians/IDProofingStepWrapper';
import { TelemedAgreementSign } from '../components/physicians/TelemedAgreementSign';
import { partnerOnboardingReturnPath } from '../lib/partnerOnboarding';

/**
 * PhysicianIDProofing Page Component
 * 
 * Main page for physician ID verification dashboard
 * 
 * Features:
 * - Hosts the ID proofing dashboard with independent steps
 * - Routes to individual step components when needed
 * - Integration with authentication system
 * - Navigation handling for completion/cancellation
 * - Access control (physician role only)
 */

export const PhysicianIDProofing: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  // Ensure user is authenticated and is a physician
  if (!user || (user.role !== 'physician' && user.role !== 'physician_readonly')) {
    navigate('/dashboard');
    return null;
  }

  // Get initial NPI from user profile if available
  const initialNPI = user.physicianData?.npiNumber || '';

  // Telemed-only clinics (no NPI, prescribing through the doctor network) don't
  // do license/selfie ID proofing — they e-sign a combined Contract + BAA instead.
  const isTelemedOnly = user.physicianData?.telemedOnly === true;

  // Hybrid clinics (own NPI + network for uncovered states) DO complete the
  // license/selfie steps, but their signature step also executes the Contract +
  // BAA — so the signature pad renders the agreement and records its version.
  const usesNetwork = isTelemedOnly || user.physicianData?.useNetworkForUncoveredStates === true;

  const handleWorkflowComplete = async () => {
    // Refresh the cached user so the dashboard reflects the new idProofingStatus
    // ('submitted') — otherwise the verification banner shows stale "pending" copy.
    try { await useAuthStore.getState().getCurrentUser(); } catch { /* non-fatal */ }
    // Navigate back to dashboard with success message
    navigate(partnerOnboardingReturnPath() || '/dashboard?idProofingComplete=true');
  };

  const handleWorkflowCancel = () => {
    // Navigate back to dashboard
    navigate(partnerOnboardingReturnPath() || '/dashboard');
  };

  // Telemed-only clinics get the single contract + BAA e-sign page for every
  // /id-proofing route (there are no license/selfie sub-steps for them).
  if (isTelemedOnly) {
    return (
      <TelemedAgreementSign
        physicianId={user._id}
        onComplete={handleWorkflowComplete}
        onCancel={handleWorkflowCancel}
      />
    );
  }

  // Determine if we're on a specific step or the main dashboard
  const isStepRoute = location.pathname !== '/id-proofing';

  if (isStepRoute) {
    return (
      <IDProofingStepWrapper
        initialNPI={initialNPI}
        physicianId={user._id}
        onComplete={handleWorkflowComplete}
        onCancel={handleWorkflowCancel}
        usesNetwork={usesNetwork}
      />
    );
  }

  return (
    <IDProofingDashboard
      initialNPI={initialNPI}
      physicianId={user._id}
      onComplete={handleWorkflowComplete}
      onCancel={handleWorkflowCancel}
    />
  );
};
